import { ReactRenderer } from '@tiptap/react';
import type {
  SuggestionProps as TipTapSuggestionProps,
  SuggestionKeyDownProps as TipTapSuggestionKeyDownProps,
} from '@tiptap/suggestion';
import tippy, { type Instance } from 'tippy.js';
import { MentionList } from './MentionList';

// Local helper types
type ReferenceClientRect = () => ClientRect | DOMRect | null;

type SuggestionProps = TipTapSuggestionProps<any, any>;

type SuggestionKeyDownProps = TipTapSuggestionKeyDownProps;

interface MentionListRef {
  onKeyDown?: (props: SuggestionKeyDownProps) => boolean | void;
}

export default {
  render: () => {
    let component: ReactRenderer;
    let popup: Instance[] | undefined;

    const createPopup = (rectGetter: ReferenceClientRect) => {
      popup = tippy('body', {
        getReferenceClientRect: () => rectGetter() ?? document.body.getBoundingClientRect(),
        appendTo: () => document.body,
        content: component.element,
        showOnCreate: true,
        interactive: true,
        trigger: 'manual',
        placement: 'bottom-start',
      }) as Instance[];
    };

    return {
      onStart: (props: SuggestionProps) => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        // props.clientRect has the shape () => DOMRect | null; pass a
        // getter through so createPopup can handle nullable returns.
        createPopup(props.clientRect);
      },

      onUpdate(props: SuggestionProps) {
        component.updateProps(props);

        if (!props.clientRect) {
          // if clientRect disappeared, hide existing popup if present
          const inst = popup?.[0];
          if (inst) inst.hide();
          return;
        }

        if (!popup) {
          // popup wasn't created in onStart (missing clientRect there), create now
          createPopup(props.clientRect);
          return;
        }

        const inst = popup[0];
        if (inst) {
          inst.setProps({
            getReferenceClientRect: () =>
              props.clientRect?.() ?? document.body.getBoundingClientRect(),
          });
        }
      },

      onKeyDown(props: SuggestionKeyDownProps) {
        if (props.event.key === 'Escape') {
          const inst = popup?.[0];
          if (inst) inst.hide();
          return true;
        }

        const ref = component.ref as unknown as MentionListRef | undefined;
        return ref?.onKeyDown ? (ref.onKeyDown(props) ?? false) : false;
      },

      onExit() {
        const inst = popup?.[0];
        if (inst) inst.destroy();
        component.destroy();
      },
    };
  },
};
